/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */
/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PersonType } from 'types/enums/PersonType'
import clsx from 'clsx'
import { toast } from 'utils/toastUtils'
import BookingAction from 'store/booking/BookingAction'
import { useTypedSelector } from 'helpers/reducers'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'
import { useDebouncedCallback } from 'utils/hooks/useDebounce'

import { useAppDispatch } from 'helpers/store'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    useBookingSellerPersonsCountSchema,
    BookingSellerPersonsCountInputs,
} from 'pages/Front/Booking/Seller/schemas/useBookingSellerPersonsCountSchema'

const DriverCountPersonType = (props: any) => {
    const { t } = useTranslation()
    return (
        <div className={clsx('flex flex-col')}>
            <div className={clsx('text-base font-bold text-gray-800')}>
                {props.personType === PersonType.Child
                    ? t('Child')
                    : t('Adult')}
            </div>
            <div className={clsx('text-xs text-gray-500')}>
                {props.personType === PersonType.Child
                    ? t('Children under 15')
                    : t('Adults over 15')}
            </div>
        </div>
    )
}

interface SellerPersonsCountProps {
    className?: string
    showTitle?: boolean
}

const SellerPersonsCount: React.FC<SellerPersonsCountProps> = ({
    className,
    showTitle = true,
}) => {
    const { t } = useTranslation()
    const bookingFilter = useTypedSelector(
        (state) => state.bookingState.bookingProcess.filter
    )
    const dispatch = useAppDispatch()
    const MAX_DRIVERS_COUNT = 500
    const MIN_DRIVERS_COUNT_ADULT = 1
    const MIN_DRIVERS_COUNT_CHILD = 0

    const BookingSellerPersonsCountSchema = useBookingSellerPersonsCountSchema()
    const {
        register,
        setValue,
        reset,
        watch,
        formState: { errors },
    } = useForm<BookingSellerPersonsCountInputs>({
        resolver: zodResolver(BookingSellerPersonsCountSchema),
        defaultValues: {
            driversCount: bookingFilter.adults,
            childrenCount: bookingFilter.children,
        },
    })

    // Update form values when filter changes (e.g. from other components)
    useEffect(() => {
        if (bookingFilter.adults !== driversCount) {
            setValue('driversCount', bookingFilter.adults)
        }
        if (bookingFilter.children !== childrenCount) {
            setValue('childrenCount', bookingFilter.children)
        }
    }, [bookingFilter.adults, bookingFilter.children, setValue])

    const limitDriversCount = (count: number, type: string): number => {
        const minDriversCount =
            type === 'childrenCount'
                ? MIN_DRIVERS_COUNT_CHILD
                : MIN_DRIVERS_COUNT_ADULT
        const maxDriversCount =
            type === 'childrenCount' ? MAX_DRIVERS_COUNT : MAX_DRIVERS_COUNT
        return count <= maxDriversCount && count >= minDriversCount
            ? count
            : count > maxDriversCount
                ? maxDriversCount
                : minDriversCount
    }

    const setDriversCountValue = (driversCountValue: number, type: string) => {
        if (isNaN(+driversCountValue)) {
            toast.warning(t('Enter numbers only'))
            return
        }
        const limit = limitDriversCount(driversCountValue, type)
        if (type == 'childrenCount') {
            setValue('childrenCount', limit)
        } else {
            setValue('driversCount', limit)
        }
        // setDriversCountHandler(limitDriversCount(limit));
    }
    const debouncedChangeHandler = useDebouncedCallback(
        (driversCountValue: number, type: string) =>
            setDriversCountValue(driversCountValue, type),
        300
    )

    const driversCount = watch('driversCount')
    const childrenCount = watch('childrenCount')

    const setDriversCountHandler = (newDriversCount: number, type: string) => {
        console.log('newDriversCount', newDriversCount)
        const newCount =
            (type === 'childrenCount' ? +childrenCount : +driversCount) +
            newDriversCount
        setDriversCountValue(newCount, type)
    }

    const driversCountDispatch = (count, childrenCount) => {
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                adults: count,
                children: childrenCount,
            })
        )
    }

    useEffect(() => {
        driversCountDispatch(driversCount, childrenCount)
    }, [driversCount, childrenCount])

    return (
        <div className={clsx('SellerPersonsCount w-full', className)}>
            <div className={'grid grid-cols-2 gap-3'}>
                {/* Adults Card */}
                <div className={'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm hover:shadow-md transition-shadow'}>
                    <div className={'flex flex-col justify-between h-full'}>
                        <div>
                            <div className={'text-sm font-bold text-gray-800 dark:text-gray-100'}>{t('Adult')}</div>
                            <div className={'text-xs text-gray-500 dark:text-gray-400'}>{t('Adults over 15')}</div>
                        </div>
                        <div className={'mt-3 flex items-center gap-2'}>
                            <button
                                className={'p-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors flex items-center justify-center'}
                                id={'DecrementPersonsCount-adult'}
                                type={'button'}
                                onClick={() => setDriversCountHandler(-1, 'driversCount')}
                            >
                                <MinusIcon className={'w-4 h-4 text-gray-700 dark:text-gray-200'} />
                            </button>
                            <input
                                {...register('driversCount')}
                                className={clsx(
                                    'flex-1 text-center h-9 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                                )}
                                inputMode={'numeric'}
                                max={MAX_DRIVERS_COUNT}
                                min={MIN_DRIVERS_COUNT_ADULT}
                                style={{
                                    appearance: 'textfield',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'textfield',
                                }}
                                type={'number'}
                                onChange={(e) =>
                                    debouncedChangeHandler(
                                        +e.target.value,
                                        'driversCount'
                                    )
                                }
                            />
                            <button
                                className={'p-1.5 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 rounded transition-colors flex items-center justify-center'}
                                id={'IncrementPersonsCount-adult'}
                                type={'button'}
                                onClick={() => setDriversCountHandler(+1, 'driversCount')}
                            >
                                <PlusIcon className={'w-4 h-4 text-white'} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Children Card */}
                <div className={'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm hover:shadow-md transition-shadow'}>
                    <div className={'flex flex-col justify-between h-full'}>
                        <div>
                            <div className={'text-sm font-bold text-gray-800 dark:text-gray-100'}>{t('Child')}</div>
                            <div className={'text-xs text-gray-500 dark:text-gray-400'}>{t('Children under 15')}</div>
                        </div>
                        <div className={'mt-3 flex items-center gap-2'}>
                            <button
                                className={'p-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors flex items-center justify-center'}
                                id={'DecrementPersonsCount-child'}
                                type={'button'}
                                onClick={() => setDriversCountHandler(-1, 'childrenCount')}
                            >
                                <MinusIcon className={'w-4 h-4 text-gray-700 dark:text-gray-200'} />
                            </button>
                            <input
                                {...register('childrenCount')}
                                className={clsx(
                                    'flex-1 text-center h-9 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                                )}
                                inputMode={'numeric'}
                                max={MAX_DRIVERS_COUNT}
                                min={MIN_DRIVERS_COUNT_CHILD}
                                style={{
                                    appearance: 'textfield',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'textfield',
                                }}
                                type={'number'}
                                onChange={(e) =>
                                    debouncedChangeHandler(
                                        +e.target.value,
                                        'childrenCount'
                                    )
                                }
                            />
                            <button
                                className={'p-1.5 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 rounded transition-colors flex items-center justify-center'}
                                id={'IncrementPersonsCount-child'}
                                type={'button'}
                                onClick={() => setDriversCountHandler(+1, 'childrenCount')}
                            >
                                <PlusIcon className={'w-4 h-4 text-white'} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SellerPersonsCount
